import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { useUser } from '../contexts/UserContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import {
    FiUsers, FiUserCheck, FiClock, FiCheckCircle,
    FiChevronLeft, FiChevronRight, FiX, FiCalendar, FiRotateCcw, FiServer,
    FiPhone, FiMail, FiBriefcase, FiToggleLeft, FiToggleRight, FiSearch, FiGitCommit,
    FiDownload, FiChevronUp, FiChevronDown, FiFilter, FiArrowUp, FiArrowDown, FiGitMerge, FiCreditCard
} from 'react-icons/fi';
import { FaIdCard } from 'react-icons/fa';
import IdCard from './feature/Id_card';
import { RiFilePdf2Fill, RiFileExcelFill, RiUser3Fill } from "react-icons/ri";

import OverallTicketChart from '../components/OverallTicketChart';
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from 'react-router-dom';
import { differenceInYears, differenceInMonths } from 'date-fns';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import EmployeeDetails from './employee_details';
import GitActivity from './git/Git_activity';
import LeaveManagement from './hrms/LeaveManagement';
import AttendanceDashboard from './hrms/AttendanceDashboard';

function HrmsDashboard() {
    const { user } = useUser();
    const [activeTab, setActiveTab] = useState('overview');

    // Role checks
    const roleIdRaw = user?.user_role_id ?? user?.role_id;
    const roleId = parseInt(roleIdRaw);
    const userId = user?.user_id;

    const isHROrAdmin = user && [1, 9].includes(roleId);
    const isClient = user && roleId === 2;
    const isEmployee = user && !isHROrAdmin && !isClient;

    return (
        <Layout noSidebar={true}>
            <div className={`bg-gray-50 min-h-screen flex flex-col ${activeTab === 'profile' ? '' : ''}`}>

                {/* Header & Tabs Container */}
                <div className="px-6 pt-6 pb-2">
                    {/* Header */}
                    <div className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-800">
                            {isHROrAdmin ? 'HRMS Dashboard' : 'Employee Dashboard'}
                        </h1>
                        <p className="text-gray-600 mt-1">
                            {isHROrAdmin ? 'Human Resource Management System' : 'Employee Performance Overview'}
                        </p>
                    </div>

                    {/* Tabs */}
                    <div className="flex flex-wrap gap-1 border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`px-3 py-2 font-medium text-xs md:text-sm md:px-4 transition-colors rounded-t-lg ${activeTab === 'overview'
                                ? 'bg-white text-blue-600 border border-gray-200 border-b-white -mb-px'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            Overview
                        </button>
                        {isHROrAdmin && (
                            <>
                                <button
                                    onClick={() => setActiveTab('employees')}
                                    className={`px-3 py-2 font-medium text-xs md:text-sm md:px-4 transition-colors rounded-t-lg ${activeTab === 'employees'
                                        ? 'bg-white text-blue-600 border border-gray-200 border-b-white -mb-px'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    Employee Details
                                </button>
                                <button
                                    onClick={() => setActiveTab('gitlab')}
                                    className={`px-3 py-2 font-medium text-xs md:text-sm md:px-4 transition-colors rounded-t-lg ${activeTab === 'gitlab'
                                        ? 'bg-white text-blue-600 border border-gray-200 border-b-white -mb-px'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    GitLab Activity
                                </button>
                            </>
                        )}
                        {isEmployee && (
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`px-3 py-2 font-medium text-xs md:text-sm md:px-4 transition-colors rounded-t-lg ${activeTab === 'profile'
                                    ? 'bg-white text-blue-600 border border-gray-200 border-b-white -mb-px'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Profile
                            </button>
                        )}
                        <button
                            onClick={() => setActiveTab('leaves')}
                            className={`px-3 py-2 font-medium text-xs md:text-sm md:px-4 transition-colors rounded-t-lg ${activeTab === 'leaves'
                                ? 'bg-white text-blue-600 border border-gray-200 border-b-white -mb-px'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                        >
                            Leave Management
                        </button>
                        {isHROrAdmin && (
                            <button
                                onClick={() => setActiveTab('attendance')}
                                className={`px-3 py-2 font-medium text-xs md:text-sm md:px-4 transition-colors rounded-t-lg ${activeTab === 'attendance'
                                    ? 'bg-white text-blue-600 border border-gray-200 border-b-white -mb-px'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Attendance Dashboard
                            </button>
                        )}
                    </div>
                </div>

                {/* Content Area - Logic for Spacing depending on Tab */}
                <div className="flex-1">
                    {activeTab === 'overview' && (
                        <div className="px-6 pb-6 pt-4">
                            <DashboardOverview user={user} isHROrAdmin={isHROrAdmin} userId={userId} roleId={roleId} setActiveTab={setActiveTab} />
                        </div>
                    )}

                    {isHROrAdmin && activeTab === 'employees' && (
                        <div className="px-6 pb-6 pt-4">
                            <EmployeeListTab />
                        </div>
                    )}

                    {isHROrAdmin && activeTab === 'gitlab' && (
                        <div className="px-6 pb-6 pt-4">
                            <GitActivity />
                        </div>
                    )}

                    {isEmployee && activeTab === 'profile' && (
                        // Render directly without extra padding wrapper to avoid duplicates
                        <EmployeeDetails />
                    )}
                    {activeTab === 'leaves' && (
                        <div className="px-6 pb-6 pt-4">
                            <LeaveManagement user={user} isHROrAdmin={isHROrAdmin} />
                        </div>
                    )}

                    {isHROrAdmin && activeTab === 'attendance' && (
                        <div className="px-6 pb-6 pt-4">
                            <AttendanceDashboard />
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

// ==========================================
// Sub-Component: Dashboard Overview
// ==========================================
function DashboardOverview({ user, isHROrAdmin, userId, roleId, setActiveTab }) {
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

    // Calendar State
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [calendarYear, setCalendarYear] = useState(new Date().getFullYear()); // Renamed from currentYear
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [showEventModal, setShowEventModal] = useState(false);
    const [eventFormData, setEventFormData] = useState({
        event_type: 'Event',
        event_title: '',
        event_description: ''
    });
    const [dateEvents, setDateEvents] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [eventToDelete, setEventToDelete] = useState(null);
    const [editingEventId, setEditingEventId] = useState(null);

    // GitLab Stats
    const [gitlabStats, setGitlabStats] = useState(null);


    // Chart State
    const [ticketYear, setTicketYear] = useState(new Date().getFullYear());
    const [analyticsYear, setAnalyticsYear] = useState(new Date().getFullYear());

    const [chartData, setChartData] = useState({ categories: [], series: [] });
    // Generate Year Options (Last 5 years)
    const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    // Leave Analytics State
    const [leaveYear, setLeaveYear] = useState(new Date().getFullYear());
    const [leaveMonth, setLeaveMonth] = useState('all');
    const [leaveAnalytics, setLeaveAnalytics] = useState(null);
    const [leaveLoading, setLeaveLoading] = useState(false);

    const fetchLeaveAnalytics = async () => {
        try {
            setLeaveLoading(true);
            const res = await api.get('/hrms/leave-analytics', {
                params: {
                    user_id: userId,
                    year: leaveYear,
                    month: leaveMonth === 'all' ? '' : leaveMonth
                }
            });
            if (res.data.status) {
                setLeaveAnalytics(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching leave analytics:', error);
        } finally {
            setLeaveLoading(false);
        }
    };

    useEffect(() => {
        if (userId && !isHROrAdmin) {
            fetchLeaveAnalytics();
        }
    }, [userId, leaveYear, leaveMonth]);

    // 1. Fetch Stats
    useEffect(() => {
        const fetchDashboardStats = async () => {
            try {
                setLoading(true);
                const response = await api.get('/hrms/dashboard-stats', {
                    params: { user_id: userId, role_id: roleId }
                });
                if (response.data.status) {
                    setStats(prev => ({ ...prev, ...response.data.data }));
                }
            } catch (error) {
                console.error('Error fetching dashboard stats:', error);
                toast.error('Failed to load dashboard statistics');
            } finally {
                setLoading(false);
            }
        };
        if (userId && roleId) fetchDashboardStats();
    }, [userId, roleId]);

    // 2. Fetch Calendar Events
    const fetchCalendarEvents = async () => {
        try {
            const response = await api.get('/hrms/calendar-events', {
                params: { month: currentMonth + 1, year: calendarYear }
            });
            if (response.data.status) {
                setCalendarEvents(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching calendar events:', error);
        }
    };

    useEffect(() => {
        fetchCalendarEvents();
    }, [currentMonth, calendarYear]);

    // 3. Fetch Chart Data & Analytics
    useEffect(() => {
        const processChartData = (res) => {
            if (res.data.data?.data) {
                const tickets = res.data.data.data;
                const months = Object.keys(tickets);
                const ticketArray = Object.values(tickets);
                const statusColors = {
                    'PENDING VERIFICATION & CLOSURE': '#1f77b4',
                    'IN PROCESS': '#ff7f0e',
                    'REOPEN': '#d62728',
                    'CLOSED': '#2ca02c',
                    'NEW': '#fffb00ff'
                };
                const statuses = [...new Set(ticketArray.flatMap((obj) => Object.keys(obj).filter((k) => k !== 'month' && k !== 'year')))];
                const seriesTemp = statuses.map((status) => ({
                    name: status,
                    data: months.map((m) => {
                        const statusData = tickets[m] && tickets[m][status];
                        if (!statusData) return 0;
                        return Object.values(statusData).reduce((sum, v) => sum + (v.count ?? 0), 0);
                    }),
                    stack: 'Ticket',
                    color: statusColors[status] || '#888888'
                }));
                setChartData({ categories: months, series: seriesTemp });
            } else {
                setChartData({ categories: [], series: [] });
            }
        };

        const fetchOverallChartData = async () => {
            try {
                const res = await api.get('/ticket/chartData', {
                    params: { user_id: userId, selectedYears: [ticketYear] }
                });
                processChartData(res);
            } catch (err) {
                console.error('Error fetching chart data:', err);
            }
        };

        const fetchEmployeePerformance = async () => {
            try {
                const res = await api.get('/ticket/chartData', {
                    params: { user_id: userId, selectedYears: [ticketYear] }
                });
                processChartData(res);
            } catch (error) {
                console.error('Error fetching employee performance:', error);
            }
        };

        if (userId && roleId) {
            if (isHROrAdmin) fetchOverallChartData();
            else fetchEmployeePerformance();
        }
    }, [userId, roleId, isHROrAdmin, ticketYear]);

    // 4. Fetch Analytics (Isolated)
    useEffect(() => {
        const fetchAnalytics = async () => {
            try {
                // Clear previous data to indicate loading/change
                setStats(prev => ({
                    ...prev,
                    monthly_overall: Array(12).fill(0),
                    monthly_my: Array(12).fill(0)
                }));

                const res = await api.get('/hrms/analytics', { params: { year: analyticsYear } });
                if (res.data.status) {
                    const { overall_avg, my_avg, monthly_overall, monthly_my } = res.data.data;
                    setStats(prev => ({
                        ...prev, overall_avg, my_avg, monthly_overall, monthly_my
                    }));
                }
            } catch (error) {
                console.error('Error fetching analytics:', error);
            }
        };

        if (userId && roleId) {
            fetchAnalytics();
        }
    }, [userId, roleId, analyticsYear]);



    // 5. Fetch GitLab Stats (Removed - Moved to GitActivity)

    // Calendar Helpers
    const formatDateForAPI = (date) => {
        const d = new Date(calendarYear, currentMonth, date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDateDisplay = (date) => {
        const d = new Date(calendarYear, currentMonth, date);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();
    const isSunday = (date) => new Date(calendarYear, currentMonth, date).getDay() === 0;
    const getEventsForDate = (date) => {
        const dateStr = formatDateForAPI(date);
        return calendarEvents.filter(event => event.event_date === dateStr);
    };

    const handleDateClick = async (date) => {
        setSelectedDate(date);
        try {
            const response = await api.get('/hrms/events-by-date', { params: { date: formatDateForAPI(date) } });
            if (response.data.status) setDateEvents(response.data.data);
        } catch (error) { console.error('Error fetching date events:', error); }
        setShowEventModal(true);
        setEventFormData({ event_type: 'Event', event_title: '', event_description: '' });
        setEditingEventId(null);
    };

    const handleEditEvent = (event) => {
        setEditingEventId(event.id);
        setEventFormData({
            event_type: event.event_type,
            event_title: event.event_title,
            event_description: event.event_description || ''
        });
    };

    const handleSaveEvent = async () => {
        if (!eventFormData.event_title.trim()) { toast.error('Event title is required'); return; }
        try {
            const url = editingEventId ? '/hrms/update-event' : '/hrms/create-event';
            const payload = {
                user_id: userId, role_id: roleId,
                event_date: formatDateForAPI(selectedDate), ...eventFormData
            };
            if (editingEventId) payload.event_id = editingEventId;
            const response = await api.post(url, payload);
            if (response.data.status) {
                toast.success(editingEventId ? 'Event updated successfully' : 'Event created successfully');
                setShowEventModal(false);
                fetchCalendarEvents();
                setEventFormData({ event_type: 'Event', event_title: '', event_description: '' });
                setEditingEventId(null);
            }
        } catch (error) {
            console.error('Error saving event:', error);
            toast.error(error.response?.data?.message || 'Failed to save event');
        }
    };

    const handleDeleteEvent = (eventId) => {
        setEventToDelete(eventId);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteEvent = async () => {
        if (!eventToDelete) return;
        try {
            const response = await api.post('/hrms/delete-event', { user_id: userId, role_id: roleId, event_id: eventToDelete });
            if (response.data.status) {
                toast.success('Event deleted successfully');
                fetchCalendarEvents();
                setDateEvents(dateEvents.filter(e => e.id !== eventToDelete));
                setShowDeleteConfirm(false);
                setEventToDelete(null);
            }
        } catch (error) {
            console.error('Error deleting event:', error);
            toast.error(error.response?.data?.message || 'Failed to delete event');
            setShowDeleteConfirm(false);
        }
    };

    const handlePrevMonth = () => {
        if (currentMonth === 0) { setCurrentMonth(11); setCalendarYear(calendarYear - 1); }
        else { setCurrentMonth(currentMonth - 1); }
    };

    const handleNextMonth = () => {
        if (currentMonth === 11) { setCurrentMonth(0); setCalendarYear(calendarYear + 1); }
        else { setCurrentMonth(currentMonth + 1); }
    };

    const getEventTypeColor = (type) => {
        const colors = {
            'Holiday': 'bg-green-100 text-green-700 border-green-300',
            'Event': 'bg-blue-100 text-blue-700 border-blue-300',
            'Meeting': 'bg-purple-100 text-purple-700 border-purple-300',
            'Deadline': 'bg-red-100 text-red-700 border-red-300'
        };
        return colors[type] || 'bg-gray-100 text-gray-700 border-gray-300';
    };


    const renderCalendar = () => {
        const daysInMonth = getDaysInMonth(currentMonth, calendarYear);
        const firstDay = getFirstDayOfMonth(currentMonth, calendarYear);
        const days = [];
        for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="calendar-day-cell empty"></div>);
        for (let date = 1; date <= daysInMonth; date++) {
            const events = getEventsForDate(date);
            const sunday = isSunday(date);
            days.push(
                <div key={date} className={`calendar-day-cell ${sunday ? 'sunday' : ''}`} onClick={() => handleDateClick(date)}>
                    <div className="date-number">{date}</div>
                    <div className="events-container">
                        {sunday && <div className="event-badge holiday"><span className="event-dot"></span><span className="event-text">Holiday</span></div>}
                        {events.slice(0, 2).map((event, idx) => (
                            <div key={idx} className={`event-badge ${event.event_type.toLowerCase()}`}><span className="event-dot"></span><span className="event-text">{event.event_title}</span></div>
                        ))}
                        {events.length > 2 && <div className="event-more">+{events.length - 2} more</div>}
                    </div>
                </div>
            );
        }
        return days;
    };

    return (
        <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {isHROrAdmin ? (
                    <>
                        <StatCard icon={<FiUsers className="text-3xl" />} title="Active Employees" value={stats.activeEmployees || 0} color="blue" loading={loading} />
                        <StatCard icon={<FiUserCheck className="text-3xl" />} title="Total Clients" value={stats.totalClients || 0} color="green" loading={loading} />
                        <StatCard icon={<FiClock className="text-3xl" />} title="Total Tickets" value={stats.totalTickets || 0} color="purple" loading={loading} />
                        <StatCard icon={<FiCheckCircle className="text-3xl" />} title="Completed This Month" value={stats.completedTicketsThisMonth || 0} color="amber" loading={loading} />
                    </>
                ) : (
                    <>
                        <StatCard icon={<FiClock className="text-3xl" />} title="Assigned Tickets" value={stats.assignedTickets || 0} color="blue" loading={loading} />
                        <StatCard icon={<FiUsers className="text-3xl" />} title="Pending Tickets" value={stats.pendingTickets || 0} color="amber" loading={loading} />
                        <StatCard icon={<FiRotateCcw className="text-3xl" />} title="Reopened Tickets" value={stats.reopenedTickets || 0} color="purple" loading={loading} />
                        <StatCard icon={<FiCheckCircle className="text-3xl" />} title="Completed This Month" value={stats.completedTicketsThisMonth || 0} color="green" loading={loading} />
                    </>
                )}
            </div>
            {/* GitLab Activity Section Removed */}

            {/* Chart and Calendar Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold text-gray-800">{isHROrAdmin ? 'Ticket Analysis - Overall' : 'My Performance'}</h3>
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">Year:</span>
                                <select
                                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-6"
                                    value={ticketYear}
                                    onChange={(e) => setTicketYear(parseInt(e.target.value))}
                                >
                                    {yearOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {chartData.series && chartData.series.length > 0 && chartData.categories && chartData.categories.length > 0 ? (
                            <OverallTicketChart collegeName="" categories={chartData.categories} series={chartData.series} height={460} />
                        ) : (
                            <div className="flex items-center justify-center h-[460px] text-gray-400">
                                <div className="text-center"><FiClock className="text-5xl mx-auto mb-2" /><p>No data available</p></div>
                            </div>
                        )}
                    </div>

                    {/* Duration Chart */}
                    <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-800">Avg Ticket Handling Time (Hours)</h3>
                            {/* Reuse global year, or allow independent if needed. User asked for year dropdown "top right of chart... for all charts". 
                                 Since they share state, showing it on both indicates it filters both. 
                             */}
                            <div className="flex items-center space-x-2">
                                <span className="text-sm text-gray-500">Year:</span>
                                <select
                                    className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-6"
                                    value={analyticsYear}
                                    onChange={(e) => setAnalyticsYear(parseInt(e.target.value))}
                                >
                                    {yearOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={{
                                chart: { type: 'spline', height: 450, backgroundColor: 'transparent' },
                                title: { text: null },
                                xAxis: {
                                    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                                    crosshair: true,
                                    title: { text: 'Month' }
                                },
                                yAxis: { min: 0, title: { text: 'Avg Hours' } },
                                plotOptions: { spline: { marker: { radius: 4, lineColor: '#666666', lineWidth: 1 }, dataLabels: { enabled: true, format: '{y}' } } },
                                tooltip: { shared: true, valueSuffix: ' hrs' },
                                series: isHROrAdmin
                                    ? [{ name: 'Company Avg', data: stats.monthly_overall || [], color: '#6366f1' }]
                                    : [
                                        { name: 'Company Avg', data: stats.monthly_overall || [], color: '#6366f1' },
                                        { name: 'My Avg', data: stats.monthly_my || [], color: '#10b981' }
                                    ]
                            }}
                        />
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Office Calendar */}
                    <div className="bg-white rounded-2xl shadow-md hover:shadow-lg transition">
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-bold text-gray-800">Office Calendar</h3>
                                <div className="flex items-center gap-2">
                                    <div className="flex items-center gap-1">
                                        <select value={currentMonth} onChange={(e) => setCurrentMonth(parseInt(e.target.value))} className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-24">
                                            {monthNames.map((month, idx) => <option key={idx} value={idx}>{month}</option>)}
                                        </select>
                                        <select value={calendarYear} onChange={(e) => setCalendarYear(parseInt(e.target.value))} className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 w-20">
                                            {[2023, 2024, 2025, 2026, 2027].map(year => <option key={year} value={year}>{year}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={handlePrevMonth} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"><FiChevronLeft size={14} /></button>
                                        <button onClick={handleNextMonth} className="p-1.5 border border-gray-300 rounded hover:bg-gray-50"><FiChevronRight size={14} /></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="p-4">
                            <div className="calendar-grid">
                                <div className="weekday-header">Sun</div><div className="weekday-header">Mon</div><div className="weekday-header">Tue</div>
                                <div className="weekday-header">Wed</div><div className="weekday-header">Thu</div><div className="weekday-header">Fri</div><div className="weekday-header">Sat</div>
                                {renderCalendar()}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="text-xs font-semibold text-gray-700 mb-2">Legend</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-green-500"></div><span className="text-[10px] text-gray-600">Holiday</span></div>
                                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div><span className="text-[10px] text-gray-600">Event</span></div>
                                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-purple-500"></div><span className="text-[10px] text-gray-600">Meeting</span></div>
                                    <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-full bg-red-500"></div><span className="text-[10px] text-gray-600">Deadline</span></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Leave Details - Employee Side */}
                    {!isHROrAdmin && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6">
                            {/* Leave Distribution Chart */}
                            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-bold text-gray-800">Leave Details</h3>
                                    <div className="flex gap-1">
                                        <select
                                            value={leaveMonth}
                                            onChange={(e) => setLeaveMonth(e.target.value)}
                                            className="border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            <option value="all">All Months</option>
                                            {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                                        </select>
                                        <select
                                            value={leaveYear}
                                            onChange={(e) => setLeaveYear(parseInt(e.target.value))}
                                            className="border border-gray-300 rounded px-2 py-1 text-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
                                        </select>
                                    </div>
                                </div>
                                {leaveAnalytics?.chart?.length > 0 ? (
                                    <HighchartsReact
                                        highcharts={Highcharts}
                                        options={{
                                            chart: { type: 'pie', height: 350, backgroundColor: 'transparent' },
                                            title: { text: null },
                                            plotOptions: {
                                                pie: {
                                                    innerSize: '65%',
                                                    dataLabels: { enabled: false },
                                                    showInLegend: true,
                                                    borderWidth: 0
                                                }
                                            },
                                            tooltip: { pointFormat: '<b>{point.y}</b> days' },
                                            legend: {
                                                layout: 'vertical',
                                                align: 'right',
                                                verticalAlign: 'middle',
                                                itemStyle: { fontSize: '11px', fontWeight: '500' },
                                                labelFormat: '{name}: <b>{y}</b>'
                                            },
                                            series: [{
                                                name: 'Days',
                                                colorByPoint: true,
                                                data: leaveAnalytics.chart
                                            }],
                                            credits: { enabled: false }
                                        }}
                                    />
                                ) : (
                                    <div className="flex items-center justify-center h-[350px] text-gray-400">
                                        <div className="text-center"><FiClock className="text-4xl mx-auto mb-2 text-gray-200" /><p className="text-sm">No activity records</p></div>
                                    </div>
                                )}
                            </div>

                            {/* Leave Summary Stats */}
                            <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition flex flex-col">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-lg font-bold text-gray-800">Account Summary</h3>
                                </div>
                                <div className="grid grid-cols-2 gap-4 flex-1">
                                    <div className="p-4 bg-blue-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">Total Leaves</p>
                                        <p className="text-2xl font-black text-blue-900">{leaveAnalytics?.stats?.total_leaves || 0}</p>
                                    </div>
                                    <div className="p-4 bg-green-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-1">Taken</p>
                                        <p className="text-2xl font-black text-green-900">{leaveAnalytics?.stats?.taken || 0}</p>
                                    </div>
                                    <div className="p-4 bg-orange-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-1">Absent</p>
                                        <p className="text-2xl font-black text-orange-900">{leaveAnalytics?.stats?.absent || 0}</p>
                                    </div>
                                    <div className="p-4 bg-purple-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-1">Request</p>
                                        <p className="text-2xl font-black text-purple-900">{leaveAnalytics?.stats?.request || 0}</p>
                                    </div>
                                    <div className="p-4 bg-emerald-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-1">Worked Days</p>
                                        <p className="text-2xl font-black text-emerald-900">{leaveAnalytics?.stats?.worked_days || 0}</p>
                                    </div>
                                    <div className="p-4 bg-pink-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-pink-600 uppercase tracking-wider mb-1">Loss of Pay</p>
                                        <p className="text-2xl font-black text-pink-900">{leaveAnalytics?.stats?.loss_of_pay || 0}</p>
                                    </div>
                                    <div className="p-4 bg-cyan-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-cyan-600 uppercase tracking-wider mb-1">Paid Permission</p>
                                        <p className="text-2xl font-black text-cyan-900">{leaveAnalytics?.stats?.paid_permission || 0}h</p>
                                    </div>
                                    <div className="p-4 bg-red-50 rounded-xl flex flex-col justify-center">
                                        <p className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-1">Excess Per.</p>
                                        <p className="text-2xl font-black text-red-900">{leaveAnalytics?.stats?.excess_permission || 0}h</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setActiveTab('leaves')}
                                    className="w-full mt-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-black hover:bg-black transition-all shadow-lg hover:shadow-xl active:scale-[0.98]"
                                >
                                    APPLY NEW LEAVE
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Event Modal */}
            {showEventModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-gray-800"><FiCalendar className="inline mr-2" />{formatDateDisplay(selectedDate)}</h3>
                            <button onClick={() => setShowEventModal(false)} className="text-gray-400 hover:text-gray-600"><FiX className="text-2xl" /></button>
                        </div>
                        <div className="p-6">
                            {dateEvents.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="font-semibold text-gray-700 mb-3">Events on this date:</h4>
                                    <div className="space-y-3">
                                        {dateEvents.map((event) => (
                                            <div key={event.id} onClick={() => isHROrAdmin && handleEditEvent(event)} className={`p-4 rounded-lg border-2 ${getEventTypeColor(event.event_type)} ${isHROrAdmin ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}>
                                                <div className="flex justify-between items-start">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2"><span className="px-2 py-1 text-xs font-semibold rounded">{event.event_type}</span><h5 className="font-semibold">{event.event_title}</h5></div>
                                                        {event.event_description && <p className="text-sm mt-2">{event.event_description}</p>}
                                                    </div>
                                                    {isHROrAdmin && <button onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }} className="ml-4 text-red-500 hover:text-red-700"><FiX /></button>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {isHROrAdmin && (
                                <div>
                                    <h4 className="font-semibold text-gray-700 mb-4">{editingEventId ? 'Edit Event:' : 'Add New Event:'}</h4>
                                    <div className="space-y-4">
                                        <div><label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label><select value={eventFormData.event_type} onChange={(e) => setEventFormData({ ...eventFormData, event_type: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"><option value="Holiday">Holiday</option><option value="Event">Event</option><option value="Meeting">Meeting</option><option value="Deadline">Deadline</option></select></div>
                                        <div><label className="block text-sm font-medium text-gray-700 mb-2">Event Title *</label><input type="text" value={eventFormData.event_title} onChange={(e) => setEventFormData({ ...eventFormData, event_title: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter event title" /></div>
                                        <div><label className="block text-sm font-medium text-gray-700 mb-2">Event Details</label><textarea value={eventFormData.event_description} onChange={(e) => setEventFormData({ ...eventFormData, event_description: e.target.value })} className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" rows="4" placeholder="Enter event details..."></textarea></div>
                                        <div className="flex gap-3 pt-4"><button onClick={handleSaveEvent} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2.5 rounded-lg transition">{editingEventId ? 'Update Event' : 'Save Event'}</button><button onClick={() => setShowEventModal(false)} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium px-6 py-2.5 rounded-lg transition">Cancel</button></div>
                                    </div>
                                </div>
                            )}
                            {!isHROrAdmin && dateEvents.length === 0 && <div className="text-center py-8 text-gray-400"><FiCalendar className="text-5xl mx-auto mb-2" /><p>No events on this date</p></div>}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Event</h3>
                        <p className="text-gray-600 mb-6">Are you sure you want to delete this event? This action cannot be undone.</p>
                        <div className="flex justify-end gap-3">
                            {/* <button onClick={() => { setShowDeleteConfirm(false); setEventToDelete(null); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Delete</button> */}
                            <button onClick={confirmDeleteEvent} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">Delete</button>
                        </div>

                    </div>
                </div>
            )}

            <style jsx>{`
        .calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 1px; background-color: #e5e7eb; border: 1px solid #e5e7eb; }
        .weekday-header { background-color: #f8fafc; padding: 8px 4px; text-align: center; font-weight: 600; font-size: 0.75rem; color: #6b7280; }
        .calendar-day-cell { background-color: white; min-height: 70px; padding: 4px; cursor: pointer; transition: background-color 0.2s; position: relative; display: flex; flex-direction: column; overflow: hidden; }
        .calendar-day-cell:hover { background-color: #f9fafb; }
        .calendar-day-cell.empty { background-color: #f9fafb; cursor: default; }
        .calendar-day-cell.sunday { background-color: #fef3c7; }
        .date-number { font-weight: 600; font-size: 0.75rem; margin-bottom: 2px; color: #374151; }
        .events-container { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .event-badge { display: flex; align-items: center; gap: 4px; font-size: 0.7rem; padding: 2px 4px; border-radius: 4px; white-space: nowrap; overflow: hidden; max-width: 100%; box-sizing: border-box; }
        .event-badge.holiday { background-color: #dcfce7; color: #166534; }
        .event-badge.event { background-color: #dbeafe; color: #1e40af; }
        .event-badge.meeting { background-color: #f3e8ff; color: #6b21a8; }
        .event-badge.deadline { background-color: #fee2e2; color: #991b1b; }
        .event-dot { width: 5px; height: 5px; border-radius: 50%; background-color: currentColor; flex-shrink: 0; }
        .event-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex-grow: 1; min-width: 0; }
        .event-more { font-size: 0.65rem; color: #6b7280; margin-top: 1px; }
      `}</style>
        </div>
    );
}

// ==========================================
// Sub-Component: Employee List Tab
// ==========================================
function EmployeeListTab() {
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [empLoading, setEmpLoading] = useState(false);

    // Search & Filter State
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive

    // Pagination & Sorting State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Modals
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusModalData, setStatusModalData] = useState(null);
    const [selectedEmployeeForIdCard, setSelectedEmployeeForIdCard] = useState(null);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]); // Bulk Selection
    const [isIdCardOpen, setIsIdCardOpen] = useState(false);

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        setEmpLoading(true);
        try {
            const res = await api.get('/hrms/employee-list');
            if (res.data.status) setEmployees(res.data.data);
        } catch (error) {
            console.error(error);
            toast.error('Failed to fetch employee list');
        } finally {
            setEmpLoading(false);
        }
    };

    const calculateExperience = (joiningDate) => {
        if (!joiningDate) return '-';
        const start = new Date(joiningDate);
        const end = new Date();
        const years = differenceInYears(end, start);
        const months = differenceInMonths(end, start) % 12;
        return `${years}y ${months}m`;
    };

    // Filter Logic
    const filteredEmployees = employees.filter(emp => {
        const search = searchTerm.toLowerCase();
        const fullName = `${emp.first_name} ${emp.last_name}`.toLowerCase();
        const matchesSearch = fullName.includes(search) ||
            (emp.email && emp.email.toLowerCase().includes(search)) ||
            (emp.designation && emp.designation.toLowerCase().includes(search)) ||
            (String(emp.user_id).includes(search));

        const matchesStatus = statusFilter === 'all'
            ? true
            : statusFilter === 'active'
                ? parseInt(emp.account_status) === 1
                : parseInt(emp.account_status) === 0;

        return matchesSearch && matchesStatus;
    });

    // Sorting Logic
    const sortedEmployees = React.useMemo(() => {
        let sortableItems = [...filteredEmployees];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];

                if (sortConfig.key === 'name') {
                    aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
                    bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
                } else if (sortConfig.key === 'experience') {
                    aVal = new Date(a.joining_date || 0);
                    bVal = new Date(b.joining_date || 0);
                } else {
                    // Safe string conversion for generic sort
                    aVal = String(aVal || '').toLowerCase();
                    bVal = String(bVal || '').toLowerCase();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [filteredEmployees, sortConfig]);

    // Pagination Logic
    const totalPages = Math.ceil(sortedEmployees.length / itemsPerPage);
    const paginatedEmployees = sortedEmployees.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Handlers
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const handlePageChange = (pageNum) => {
        setCurrentPage(pageNum);
    };

    const handleExportExcel = async () => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Employee List');

            // 1. Title
            worksheet.mergeCells('A1:H1');
            const titleCell = worksheet.getCell('A1');
            titleCell.value = 'eNova Software and Hardware Solutions P Ltd';
            titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            titleCell.font = { name: 'Arial', size: 16, bold: true };

            // 2. SubTitle
            worksheet.mergeCells('A2:H2');
            const subTitleCell = worksheet.getCell('A2');
            subTitleCell.value = 'Employee List';
            subTitleCell.alignment = { vertical: 'middle', horizontal: 'center' };
            subTitleCell.font = { name: 'Arial', size: 12, bold: true };

            // 3. Generation Date
            worksheet.mergeCells('A3:H3');
            const dateCell = worksheet.getCell('A3');
            dateCell.value = `Generated on: ${new Date().toLocaleString()}`;
            dateCell.alignment = { vertical: 'middle', horizontal: 'center' };
            dateCell.font = { name: 'Arial', size: 10, bold: false };

            // Spacer
            worksheet.addRow([]);

            // 4. Headers
            const headerTerms = ['ID', 'Name', 'Designation', 'Email', 'Mobile', 'Join Date', 'Experience', 'Status'];
            const headerRow = worksheet.addRow(headerTerms);

            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.alignment = { horizontal: 'center' };
                cell.border = { bottom: { style: 'thin' } };
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFEEEEEE' }
                };
            });

            // 5. Data
            sortedEmployees.forEach(emp => {
                worksheet.addRow([
                    emp.user_id,
                    `${emp.first_name} ${emp.last_name}`,
                    emp.designation,
                    emp.email,
                    emp.mobile,
                    emp.joining_date,
                    calculateExperience(emp.joining_date),
                    emp.account_status == 1 ? 'Active' : 'Inactive'
                ]);
            });

            // 6. Column Widths
            worksheet.columns = [
                { width: 10 }, // ID
                { width: 25 }, // Name
                { width: 25 }, // Designation
                { width: 30 }, // Email
                { width: 15 }, // Mobile
                { width: 15 }, // Join Date
                { width: 15 }, // Experience
                { width: 10 }  // Status
            ];

            // 7. Write and Save
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Employee_List_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            console.error("Excel Export Error:", error);
            toast.error("Failed to export Excel");
        }
    };

    const handleExportPDF = async () => {
        try {
            const loadingToast = toast.loading('Generating PDF...');
            const response = await api.get('/hrms/export-employee-list-pdf', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Employee_List.pdf');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success('PDF Downloaded', { id: loadingToast });
        } catch (error) {
            console.error('PDF Export Error:', error);
            toast.error('Failed to export PDF');
        }
    };

    const handleStatusToggle = (e, empId, currentStatus) => {
        e.stopPropagation();
        const newStatus = currentStatus == 1 ? 0 : 1;
        setStatusModalData({ empId, newStatus });
        setShowStatusModal(true);
    };

    const confirmStatusChange = async () => {
        if (!statusModalData) return;
        const { empId, newStatus } = statusModalData;
        try {
            const res = await api.post('/hrms/toggle-status', { user_id: empId, status: newStatus });
            if (res.data.status) {
                toast.success('Status updated successfully');
                setEmployees(prev => prev.map(emp => emp.user_id === empId ? { ...emp, account_status: newStatus } : emp));
                setShowStatusModal(false);
                setStatusModalData(null);
            }
        } catch (error) {
            console.error(error);
            toast.error('Failed to update status');
            setShowStatusModal(false);
        }
    };

    // Bulk Selection Handlers
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Select all currently filtered employees
            const allIds = filteredEmployees.map(emp => emp.user_id);
            setSelectedEmployeeIds(allIds);
        } else {
            setSelectedEmployeeIds([]);
        }
    };

    const handleSelectRow = (e, id) => {
        e.stopPropagation();
        if (e.target.checked) {
            setSelectedEmployeeIds(prev => [...prev, id]);
        } else {
            setSelectedEmployeeIds(prev => prev.filter(empId => empId !== id));
        }
    };

    // Helper for Sort Icon
    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) return <span className="text-gray-300 ml-1"><FiFilter className="inline w-3 h-3 opacity-0 group-hover:opacity-50" /></span>;
        return sortConfig.direction === 'asc'
            ? <FiChevronUp className="inline ml-1 text-blue-600" />
            : <FiChevronDown className="inline ml-1 text-blue-600" />;
    };

    // Helper for Pagination Buttons
    const getPageNumbers = () => {
        const pages = [];
        let start = Math.max(1, currentPage - 2);
        let end = Math.min(totalPages, start + 4);

        if (end - start < 4) {
            start = Math.max(1, end - 4);
        }

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col h-full">
            {/* Toolbar */}
            <div className="p-6 border-b border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 bg-white">
                <div className="flex items-center gap-2">
                    <h3 className="text-xl font-bold text-gray-800">Employee List</h3>
                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded-full">{filteredEmployees.length}</span>
                </div>

                <div className="flex flex-col md:flex-row items-center gap-3 w-full md:w-auto">
                    {/* Page Size Selector (Moved Top) */}
                    <div className="relative">
                        <select
                            value={itemsPerPage}
                            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                            className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 py-2 pl-3 pr-8 outline-none cursor-pointer hover:bg-white transition-colors appearance-none"
                            title="Rows per page"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={999999}>All</option>
                        </select>
                        {/* <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={14} /> */}
                    </div>

                    {/* Status Filter (Icon Only) */}
                    <div className="relative group">
                        <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-colors border border-gray-300 rounded-lg ${statusFilter !== 'all' ? 'bg-blue-50 border-blue-200 text-blue-600' : 'bg-transparent text-gray-500 group-hover:bg-gray-50'}`}>
                            <FiFilter size={18} />
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            className="w-10 h-10 opacity-0 cursor-pointer absolute inset-0 z-10"
                            title="Filter Status"
                        >
                            <option value="all" className="text-gray-700">All Status</option>
                            <option value="active" className="text-gray-700">Active</option>
                            <option value="inactive" className="text-gray-700">Inactive</option>
                        </select>
                    </div>

                    {/* Search */}
                    <div className="relative w-full md:w-64">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search employees..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white transition-colors text-sm"
                        />
                    </div>

                    {/* Exports */}
                    <div className="flex gap-2">
                        {selectedEmployeeIds.length > 0 && (
                            <button
                                onClick={() => {
                                    setSelectedEmployeeForIdCard(null); // Clear single selection
                                    setIsIdCardOpen(true);
                                }}
                                className="group flex items-center gap-2 px-4 py-2 bg-[#0F172A] hover:bg-black text-white rounded-lg transition-all shadow-sm"
                                title="Bulk Download ID Cards"
                            >
                                <div className="flex items-center justify-center w-5 h-5 bg-white rounded-full text-[#0F172A] text-xs font-bold">
                                    {selectedEmployeeIds.length}
                                </div>
                                <div className="flex items-center gap-1.5 font-semibold text-sm">
                                    <FaIdCard className="text-white" size={16} />
                                    <span>Bulk Download</span>
                                </div>
                            </button>
                        )}
                        <button onClick={handleExportExcel} className="p-2 text-green-600 rounded hover:bg-green-50 transition-colors shadow-sm border border-gray-200" title="Export to Excel"><RiFileExcelFill size={20} /></button>
                        <button onClick={handleExportPDF} className="p-2 text-red-600 rounded hover:bg-red-50 transition-colors shadow-sm border border-gray-200" title="Export to PDF"><RiFilePdf2Fill size={20} /></button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto flex-1">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr className="text-gray-600 text-xs font-bold uppercase tracking-wider border-b border-gray-200">
                            <th className="px-6 py-4">
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={filteredEmployees.length > 0 && selectedEmployeeIds.length === filteredEmployees.length}
                                    className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4 cursor-pointer"
                                />
                            </th>
                            <th className="px-6 py-4 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('name')}>
                                Employee <SortIcon column="name" />
                            </th>
                            <th className="px-6 py-4 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('user_id')}>
                                ID <SortIcon column="user_id" />
                            </th>
                            <th className="px-6 py-4 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('designation')}>
                                Designation <SortIcon column="designation" />
                            </th>
                            <th className="px-6 py-4 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('joining_date')}>
                                Join Date <SortIcon column="joining_date" />
                            </th>
                            <th className="px-6 py-4 cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('experience')}>
                                Experience <SortIcon column="experience" />
                            </th>
                            <th className="px-6 py-4">Contact</th>
                            <th className="px-6 py-4 text-center">ID Card</th>
                            <th className="px-6 py-4 text-center cursor-pointer group hover:bg-gray-100 transition-colors" onClick={() => requestSort('account_status')}>
                                Status <SortIcon column="account_status" />
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {empLoading ? (
                            <tr><td colSpan="8" className="px-6 py-20 text-center text-gray-500"><div className="flex flex-col justify-center items-center gap-3"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div><span>Loading employees...</span></div></td></tr>
                        ) : paginatedEmployees.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="px-6 py-20 text-center text-gray-500">
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="bg-gray-100 p-4 rounded-full mb-3"><FiUsers className="text-3xl text-gray-400" /></div>
                                        <p className="text-lg font-medium text-gray-900">No employees found</p>
                                        <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedEmployees.map((emp) => (
                                <tr key={emp.user_id} onClick={() => navigate(`/employee-details?user_id=${emp.user_id}`)} className={`hover:bg-blue-50/50 cursor-pointer transition-colors group ${selectedEmployeeIds.includes(emp.user_id) ? 'bg-blue-50' : ''}`}>
                                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            onChange={(e) => handleSelectRow(e, emp.user_id)}
                                            checked={selectedEmployeeIds.includes(emp.user_id)}
                                            className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4 cursor-pointer"
                                        />
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            {emp.profile_image ? (
                                                <img src={emp.profile_image} alt="" className="w-10 h-10 rounded-full object-cover border border-gray-200 shadow-sm" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-700 font-bold border border-white shadow-sm ring-1 ring-blue-50 text-sm">
                                                    {emp.first_name?.[0]}{emp.last_name?.[0]}
                                                </div>
                                            )}
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-semibold text-gray-900 text-sm">{emp.first_name} {emp.last_name}</p>
                                                    {/* Trend Indicator based on Work (Simulated with ID for now as per data availability) */}
                                                    {emp.user_id % 2 === 0 ? (
                                                        <FiArrowUp className="text-green-500" size={14} title="Performance Trending Up" />
                                                    ) : (
                                                        <FiArrowDown className="text-red-500" size={14} title="Performance Trending Down" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-gray-500 group-hover:text-blue-600 transition-colors">{emp.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className="font-mono text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                                            #{String(emp.user_id).padStart(4, '0')}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                                        {emp.designation || <span className="text-gray-400 italic">None</span>}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {emp.joining_date ? new Date(emp.joining_date).toLocaleDateString('en-GB') : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {calculateExperience(emp.joining_date)}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        <div className="flex flex-col gap-1.5">
                                            {emp.mobile ? (
                                                <div className="flex items-center gap-2 text-xs">
                                                    <FiPhone className="text-gray-400" size={12} />
                                                    <span>{emp.mobile}</span>
                                                </div>
                                            ) : <span className="text-gray-400 text-xs italic">No phone</span>}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => {
                                                setSelectedEmployeeForIdCard(emp.user_id);
                                                setSelectedEmployeeIds([]); // Clear bulk selection if clicking single icon
                                                setIsIdCardOpen(true);
                                            }}
                                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-all"
                                            title="Generate ID Card"
                                        >
                                            <FaIdCard size={18} />
                                        </button>
                                    </td>
                                    <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex flex-col items-center gap-1">
                                            <button
                                                onClick={(e) => handleStatusToggle(e, emp.user_id, emp.account_status)}
                                                className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${emp.account_status == 1 ? 'bg-green-500' : 'bg-gray-200'}`}
                                            >
                                                <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${emp.account_status == 1 ? 'translate-x-4' : 'translate-x-0'}`} />
                                            </button>
                                            <span className={`text-[10px] font-bold uppercase tracking-wide ${emp.account_status == 1 ? 'text-green-600' : 'text-gray-400'}`}>
                                                {emp.account_status == 1 ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-sm text-gray-600">
                    <span className="hidden md:inline">
                        Showing <span className="font-semibold text-gray-900">{filteredEmployees.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}</span> to <span className="font-semibold text-gray-900">{Math.min(currentPage * itemsPerPage, filteredEmployees.length)}</span> of <span className="font-semibold text-gray-900">{filteredEmployees.length}</span> results
                    </span>
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 transition"
                    >
                        <FiChevronLeft />
                    </button>

                    <div className="flex items-center gap-1">
                        {getPageNumbers().map(pageNum => (
                            <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`w-8 h-8 rounded text-sm font-medium transition-colors ${currentPage === pageNum
                                    ? 'bg-[#C30010] text-white shadow-sm'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                    }`}
                            >
                                {pageNum}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages || totalPages === 0}
                        className="p-2 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 transition"
                    >
                        <FiChevronRight />
                    </button>
                </div>
            </div>

            {/* ID Card Modal */}
            {isIdCardOpen && (
                <IdCard
                    employeeId={selectedEmployeeForIdCard}
                    employeeIds={selectedEmployeeIds} // Pass bulk IDs
                    isOpen={isIdCardOpen}
                    onClose={() => setIsIdCardOpen(false)}
                    onUpdateSuccess={fetchEmployees}
                />
            )}

            {/* Status Modal Confirm */}
            {showStatusModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 animate-in fade-in zoom-in duration-200 border border-gray-100">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Confirm Status Change</h3>
                        <p className="text-gray-600 mb-6 leading-relaxed">
                            Are you sure you want to <strong>{statusModalData?.newStatus === 1 ? 'activate' : 'deactivate'}</strong> this employee?
                            {statusModalData?.newStatus === 0 && <span className="block mt-2 text-red-500 text-sm font-medium bg-red-50 p-2 rounded">Warning: They will lose access to the system immediately.</span>}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setShowStatusModal(false)} className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition">Cancel</button>
                            <button onClick={confirmStatusChange} className={`px-5 py-2.5 text-white font-medium rounded-lg transition shadow-md hover:shadow-lg ${statusModalData?.newStatus === 1 ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>Yes, Confirm</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ==========================================
// Sub-Component: Stat Card
// ==========================================
function StatCard({ icon, title, value, color, loading }) {
    const colorClasses = {
        blue: 'bg-blue-100 text-blue-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600',
        amber: 'bg-amber-100 text-amber-600'
    };

    return (
        <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition p-6">
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <p className="text-gray-500 text-sm font-medium mb-2">{title}</p>
                    {loading ? <div className="h-8 w-20 bg-gray-200 animate-pulse rounded"></div> : <h3 className="text-3xl font-bold text-gray-800">{value}</h3>}
                </div>
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>{icon}</div>
            </div>
        </div>
    );
}

// Export Main Component
export default HrmsDashboard;

